import { Sprout, Scissors, Leaf, Snowflake, Wand2, Building2, Shovel, ArrowRight, Check, Home } from "lucide-react";
import { SERVICES, PACKAGES } from "@/lib/constants";
import { Button } from "@/components/ui/button";

const ICONS = {
  sprout: Sprout,
  scissors: Scissors,
  leaf: Leaf,
  snowflake: Snowflake,
  "wand-2": Wand2,
  "building-2": Building2,
  shovel: Shovel,
};

function PackageCard({ pkg, dark, onCta }) {
  const Icon = pkg.key === "paket-gewerbe" ? Building2 : Home;
  return (
    <div
      data-testid={`package-card-${pkg.key}`}
      className={`relative rounded-2xl p-8 flex flex-col h-full transition-transform duration-300 hover:-translate-y-1 ${
        dark
          ? "bg-[#2C3E2D] text-white border border-[#3F5540]"
          : "bg-white text-[#1A201A] border border-[#E5E7EB]"
      }`}
    >
      <div className={`absolute top-6 right-6 text-[10px] font-medium uppercase tracking-wider px-2.5 py-1 rounded-full ${
        dark ? "bg-[#6B7F3E] text-white" : "bg-[#E8ECE1] text-[#2C3E2D]"
      }`}>
        {pkg.tag}
      </div>

      <Icon className={`w-9 h-9 mb-5 ${dark ? "text-[#C4CFB1]" : "text-[#6B7F3E]"}`} strokeWidth={1.5} />
      <div className={`text-xs font-medium uppercase tracking-wider mb-1 ${
        dark ? "text-[#C4CFB1]" : "text-[#6B7F3E]"
      }`}>
        {pkg.subtitle}
      </div>
      <h3 className="font-display text-2xl font-semibold mb-3 leading-tight">
        {pkg.title.replace(/^Rundum-Sorglos-Paket · /, "")}
        <span className={`block text-[13px] font-normal mt-1 ${dark ? "text-[#B8C1A9]" : "text-[#5C6B5D]"}`}>
          im Rundum-Sorglos-Paket
        </span>
      </h3>
      <p className={`text-sm leading-relaxed mb-6 ${dark ? "text-[#B8C1A9]" : "text-[#4A5D4E]"}`}>
        {pkg.desc}
      </p>

      <ul className="space-y-2 mb-6 flex-1">
        {pkg.inclusions.map((it) => (
          <li key={it} className="flex items-start gap-2 text-sm">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${dark ? "text-[#C4CFB1]" : "text-[#6B7F3E]"}`} strokeWidth={2} />
            <span className={dark ? "text-white/90" : "text-[#2C3E2D]"}>{it}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onCta}
        data-testid={`package-cta-${pkg.key}`}
        className={`self-start rounded-full ${
          dark
            ? "bg-white text-[#2C3E2D] hover:bg-[#E8ECE1]"
            : "bg-[#6B7F3E] text-white hover:bg-[#5A6B34]"
        }`}
      >
        Paket anfragen
        <ArrowRight className="ml-1 w-4 h-4" />
      </Button>
    </div>
  );
}

function SonderpflegeCard({ svc, onCta }) {
  const Icon = ICONS[svc.icon];
  return (
    <button
      onClick={onCta}
      data-testid={`service-card-${svc.key}`}
      className="sm:col-span-2 lg:col-span-12 bg-[#F5F6F0] text-[#1A201A] rounded-2xl overflow-hidden text-left group hover:-translate-y-1 hover:shadow-md transition-all duration-300 border border-[#E5E7EB] hover:border-[#6B7F3E]/40 grid lg:grid-cols-12"
    >
      <div className="lg:col-span-5 relative h-56 sm:h-80 lg:h-auto overflow-hidden">
        <img
          src="/marvin_baumfaellung.jpg"
          alt="Marvin Blessing mit Schutzausrüstung bei der Baumfällung mit der Motorsäge"
          width={1600}
          height={1067}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover object-[40%_20%] transition-transform duration-700 group-hover:scale-[1.03]"
        />
      </div>
      <div className="lg:col-span-7 p-6 lg:p-8">
        <div className="flex items-start justify-between mb-4">
          <Icon className="w-8 h-8 text-[#6B7F3E] transition-transform duration-300 group-hover:scale-110" strokeWidth={1.5} />
          <span className="text-[10px] font-medium uppercase tracking-wider text-[#6B7F3E] bg-white/60 px-2 py-0.5 rounded-full">
            Einzelleistungen
          </span>
        </div>
        <h3 className="font-display text-xl font-medium mb-2">{svc.title}</h3>
        <p className="text-sm leading-relaxed text-[#5C6B5D] mb-4">{svc.desc}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mb-4">
          {svc.items.map((it) => (
            <div key={it} className="flex items-start gap-1.5 text-xs text-[#2C3E2D]">
              <span className="text-[#6B7F3E] mt-0.5">›</span>
              <span>{it}</span>
            </div>
          ))}
        </div>
        <div className="text-xs font-medium uppercase tracking-wider text-[#6B7F3E] opacity-0 group-hover:opacity-100 transition-opacity">
          Einzelleistung anfragen →
        </div>
      </div>
    </button>
  );
}

export default function Services({ onCta }) {
  const single = SERVICES.filter((s) => s.key !== "sonder");
  const sonder = SERVICES.find((s) => s.key === "sonder");

  return (
    <section id="leistungen" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <div className="text-sm font-medium text-[#6B7F3E] uppercase tracking-wider mb-3">
              Leistungen
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1A201A] tracking-tight">
              Ein Team & <span className="text-[#6B7F3E]">Alles Rund ums Haus</span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-[#4A5D4E] leading-relaxed">
              Ob als einmalige Aktion, gezielte Einzelleistung oder ganzjährige Rundum-Betreuung –
              wir übernehmen alle Arbeiten rund um Ihr Grundstück, sauber, termingerecht und
              mit Blick fürs Detail.
            </p>
          </div>
        </div>

        {/* Rundum-Sorglos-Pakete */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px bg-[#C4CFB1] flex-1" />
            <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#6B7F3E]">
              Rundum-Sorglos-Pakete
            </div>
            <div className="h-px bg-[#C4CFB1] flex-1" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <PackageCard pkg={PACKAGES[0]} dark={false} onCta={onCta} />
            <PackageCard pkg={PACKAGES[1]} dark={true} onCta={onCta} />
          </div>
        </div>

        {/* Einzelleistungen */}
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px bg-[#C4CFB1] flex-1" />
          <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#6B7F3E]">
            Einzelleistungen
          </div>
          <div className="h-px bg-[#C4CFB1] flex-1" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 auto-rows-[minmax(180px,auto)] gap-5">
          {single.map((s, i) => {
            const Icon = ICONS[s.icon];
            // asymmetric spans for 5 tiles (rasen, hecke, laub, winter, objekt) – two full rows,
            // Sonderpflege with photo spans the row below
            const span = [
              "lg:col-span-5",
              "lg:col-span-4",
              "lg:col-span-3",
              "lg:col-span-6",
              "sm:col-span-2 lg:col-span-6",
            ][i];
            const dark = false;
            const bg = "bg-[#F5F6F0] text-[#1A201A]";
            const iconColor = "text-[#6B7F3E]";
            return (
              <button
                key={s.key}
                onClick={onCta}
                data-testid={`service-card-${s.key}`}
                className={`${bg} ${span} rounded-2xl p-6 text-left group hover:-translate-y-1 hover:shadow-md transition-all duration-300 border border-transparent hover:border-[#6B7F3E]/30`}
              >
                <Icon className={`w-8 h-8 ${iconColor} mb-4 transition-transform duration-300 group-hover:scale-110`} strokeWidth={1.5} />
                <h3 className="font-display text-xl font-medium mb-2">{s.title}</h3>
                <p className={`text-sm leading-relaxed ${dark ? "text-[#B8C1A9]" : "text-[#5C6B5D]"}`}>
                  {s.desc}
                </p>
                <div className={`mt-4 text-xs font-medium uppercase tracking-wider ${dark ? "text-[#C4CFB1]" : "text-[#6B7F3E]"} opacity-0 group-hover:opacity-100 transition-opacity`}>
                  Anfragen →
                </div>
              </button>
            );
          })}
          {/* Sonderpflege full-width detail card */}
          <SonderpflegeCard svc={sonder} onCta={onCta} />
        </div>
      </div>
    </section>
  );
}
